import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { DashboardSnapshot } from '../api/dashboard.api';

type RiskRow = DashboardSnapshot['risks'][number];
type Cell = { p: number; i: number; count: number };

function isValidPI(n: number | undefined | null) {
    return typeof n === 'number' && n >= 1 && n <= 5;
}

/** RiskHeatmap.tsx 기준 색상 (score = p*i) */
function bgByScore(score: number) {
    if (score >= 15) return 'bg-rose-300';
    if (score >= 8) return 'bg-amber-300';
    if (score >= 1) return 'bg-emerald-300';
    return 'bg-gray-100';
}
function bandByScore(score: number): 'HIGH' | 'MED' | 'LOW' | 'NONE' {
    if (score >= 15) return 'HIGH';
    if (score >= 8) return 'MED';
    if (score >= 1) return 'LOW';
    return 'NONE';
}

function riskScore(r: RiskRow) {
    const p = r.probability ?? 0;
    const i = r.impact ?? 0;
    if (!isValidPI(p) || !isValidPI(i)) return 0;
    return p * i;
}

export default function RiskHeatMini({
    risks,
    /** Heatmap 셀 높이 */
    cellH = 34,
    /** Heatmap 셀 간격 */
    cellGap = 8,
    /** Heatmap 전체 폭 비율 (0~1), 기본 60% */
    heatmapScale = 1,
}: {
    risks: RiskRow[];
    cellH?: number;
    cellGap?: number;
    heatmapScale?: number;
}) {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();

    const P = [1, 2, 3, 4, 5]; // Impact
    const ProbRows = [5, 4, 3, 2, 1]; // Probability (위→아래)

    const { cells, topRisks } = useMemo(() => {
        const grid: Record<string, Cell> = {};
        for (let p = 1; p <= 5; p++) {
            for (let i = 1; i <= 5; i++) {
                grid[`${p}-${i}`] = { p, i, count: 0 };
            }
        }

        for (const r of risks ?? []) {
            const p = r.probability ?? 0;
            const i = r.impact ?? 0;
            if (isValidPI(p) && isValidPI(i)) {
                grid[`${p}-${i}`].count += 1;
            }
        }

        const top = [...(risks ?? [])]
            .sort((a, b) => riskScore(b) - riskScore(a))
            .slice(0, 6);   // top risks 숫자 조정은 여기서

        return { cells: Object.values(grid), topRisks: top };
    }, [risks]);

    const cellCount = (prob: number, impact: number) =>
        cells.find((x) => x.p === prob && x.i === impact)!.count;

    const goRiskPageByCell = (prob: number, impact: number) => {
        if (!projectId) return;
        const score = prob * impact;
        const band = bandByScore(score);
        navigate(`/projects/${projectId}/risks?pi=${prob}-${impact}&score=${score}&band=${band}`);
    };

    const goRiskEditor = (riskId: string) => {
        if (!projectId) return;
        navigate(`/projects/${projectId}/risks?focus=${encodeURIComponent(riskId)}`);
    };

    const scalePct = Math.max(0.2, Math.min(1, heatmapScale)) * 100;

    return (
        <div className="grid h-full w-full grid-cols-1 items-start gap-6 md:grid-cols-2">
            {/* ================= Left: Heatmap ================= */}
            <div className="w-full">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-medium text-gray-900">Risk Heatmap</div>
                    <div className="flex items-center gap-2 text-[11px] text-gray-600">
                        <span className="inline-flex items-center gap-1">
                            <span className="inline-block w-3 h-3 rounded bg-emerald-300" /> Low
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <span className="inline-block w-3 h-3 rounded bg-amber-300" /> Med
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <span className="inline-block w-3 h-3 rounded bg-rose-300" /> High
                        </span>
                    </div>
                </div>

                {/* Heatmap (scale 적용) */}
                <div className="mt-3 w-full overflow-x-auto">
                    <div className="mx-auto min-w-[280px]" style={{ width: `${scalePct}%` }}>
                        {/* <div className="pt-6 text-[10px] text-gray-500 rotate-[-90deg] origin-top-left translate-y-[54px]">
                        Probability →
                    </div> */}
                        {/* X labels */}
                        <div className="flex items-end" style={{ columnGap: `${cellGap}px` }}>
                            <div className="w-10" />
                            <div className="flex flex-1" style={{ columnGap: `${cellGap}px` }}>
                                {P.map((impact) => (
                                    <div key={impact} className="flex-1 text-center text-[11px] text-gray-500">
                                        I{impact}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Grid */}
                        <div className="mt-1">
                            {ProbRows.map((prob) => (
                                <div key={prob} className="mt-1 flex items-center" style={{ columnGap: `${cellGap}px` }}>
                                    {/* <div className="w-10 text-[11px] text-gray-500 text-right pr-1">P{prob}</div> */}
                                    <div className="flex flex-1" style={{ columnGap: `${cellGap}px` }}>
                                        {P.map((impact) => {
                                            const c = cellCount(prob, impact);
                                            const score = prob * impact;
                                            return (
                                                <button
                                                    key={impact}
                                                    type="button"
                                                    onClick={() => goRiskPageByCell(prob, impact)}
                                                    className={`flex flex-1 items-center justify-center rounded-md border ${bgByScore(
                                                        score,
                                                    )} transition hover:opacity-90`}
                                                    style={{ height: `${cellH}px` }}
                                                    title={`P${prob}×I${impact} (${score})`}
                                                >
                                                    <span className="text-xs font-semibold text-gray-900">
                                                        {c || ''}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* <div className="mt-2 text-[11px] text-gray-500 text-right">Impact →</div> */}
                    </div>
                </div>
            </div>

            {/* ================= Right: Top Risks ================= */}
            <div className="w-full">
                <div className="text-sm font-medium text-gray-900">Top (6) Risks</div>

                <ul className="mt-4 divide-y rounded-xl border bg-white">
                    {topRisks.length === 0 ? (
                        <li className="p-3 text-sm text-gray-500">—</li>
                    ) : (
                        topRisks.map((r) => {
                            const score = riskScore(r);
                            return (
                                <li
                                    key={r.id}
                                    className="flex items-center justify-between gap-3 p-3 cursor-pointer hover:bg-gray-50"
                                    onClick={() => goRiskEditor(r.id)}
                                >
                                    <span className="truncate text-sm font-medium text-gray-900">
                                        {r.title}
                                    </span>
                                    <span
                                        className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] border ${bgByScore(
                                            score,
                                        )}`}
                                    >
                                        {score}
                                    </span>
                                </li>
                            );
                        })
                    )}
                </ul>

                <div className="mt-2 text-[11px] text-gray-500">
                    * Heatmap 클릭 → Risk 목록 필터링  
                    <br />
                    * Risk 클릭 → Risk 에디터 열기
                </div>
            </div>
        </div>
    );
}
